import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Users, GraduationCap, CalendarCheck, MessageSquare,
  FileText, Target, CalendarDays, Send, ShieldAlert, LogOut, Menu, HeartHandshake, Type,
  Siren, BarChart3, FileCheck, ClipboardList, Receipt, Gauge, BookOpen, Palette, Video, Layers, DoorOpen
} from "lucide-react";
import { useState } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { NAV_ART } from "@/constants/images";
import EmergencyBell from "@/components/EmergencyBell";
import InboxBell from "@/components/InboxBell";
import SmartSearchBar from "@/components/SmartSearchBar";

const ROLE_LABEL = { admin: "الإدارة", teacher: "معلم/معالج", parent: "ولي أمر" };

function navFor(role) {
  const common = [
    { to: "/", label: "لوحة التحكم", icon: LayoutDashboard, testid: "nav-dashboard" },
  ];
  if (role === "admin") {
    return [
      ...common,
      { to: "/children", label: "الطلاب", icon: Users, testid: "nav-children" },
      { to: "/staff", label: "الموظفون", icon: GraduationCap, testid: "nav-staff" },
      { to: "/rooms", label: "خريطة القاعات", icon: DoorOpen, testid: "nav-rooms" },
      { to: "/attendance", label: "الحضور", icon: CalendarCheck, testid: "nav-attendance" },
      { to: "/emergency", label: "الطوارئ", icon: Siren, testid: "nav-emergency" },
      { to: "/messages", label: "الرسائل والإعلانات", icon: MessageSquare, testid: "nav-messages" },
      { to: "/daily-reports", label: "التقارير اليومية", icon: FileText, testid: "nav-reports" },
      { to: "/monthly", label: "التقارير الشهرية", icon: BarChart3, testid: "nav-monthly" },
      { to: "/analytics", label: "لوحة التحليلات", icon: Gauge, testid: "nav-analytics" },
      { to: "/evaluations", label: "التقييمات", icon: ClipboardList, testid: "nav-evaluations" },
      { to: "/invoices", label: "الفواتير", icon: Receipt, testid: "nav-invoices" },
      { to: "/articles-admin", label: "المقالات العلمية", icon: BookOpen, testid: "nav-articles-admin" },
      { to: "/videos", label: "مكتبة الفيديوهات", icon: Video, testid: "nav-videos" },
      { to: "/playlists", label: "قوائم التشغيل", icon: Layers, testid: "nav-playlists" },
      { to: "/landing-settings", label: "تخصيص الصفحة الرئيسية", icon: Palette, testid: "nav-landing-settings" },
      { to: "/iep", label: "الخطط الفردية (IEP)", icon: Target, testid: "nav-iep" },
      { to: "/sessions", label: "الجلسات", icon: CalendarDays, testid: "nav-sessions" },
      { to: "/whatsapp", label: "سجل الواتساب", icon: Send, testid: "nav-whatsapp" },
      { to: "/whatsapp-templates", label: "قوالب واتساب", icon: FileCheck, testid: "nav-wa-templates" },
      { to: "/audit", label: "سجل التدقيق", icon: ShieldAlert, testid: "nav-audit" },
    ];
  }
  if (role === "teacher") {
    return [
      ...common,
      { to: "/children", label: "طلابي", icon: Users, testid: "nav-children" },
      { to: "/attendance", label: "الحضور", icon: CalendarCheck, testid: "nav-attendance" },
      { to: "/emergency", label: "الطوارئ", icon: Siren, testid: "nav-emergency" },
      { to: "/messages", label: "الرسائل", icon: MessageSquare, testid: "nav-messages" },
      { to: "/daily-reports", label: "التقارير اليومية", icon: FileText, testid: "nav-reports" },
      { to: "/iep", label: "الخطط الفردية", icon: Target, testid: "nav-iep" },
      { to: "/evaluations", label: "التقييمات", icon: ClipboardList, testid: "nav-evaluations" },
      { to: "/sessions", label: "الجلسات", icon: CalendarDays, testid: "nav-sessions" },
      { to: "/videos", label: "مكتبة الفيديوهات", icon: Video, testid: "nav-videos" },
      { to: "/playlists", label: "قوائم التشغيل", icon: Layers, testid: "nav-playlists" },
    ];
  }
  return [
    ...common,
    { to: "/children", label: "ملف طالبي", icon: Users, testid: "nav-children" },
    { to: "/messages", label: "التواصل مع المعلم", icon: MessageSquare, testid: "nav-messages" },
    { to: "/daily-reports", label: "التقارير اليومية", icon: FileText, testid: "nav-reports" },
    { to: "/monthly", label: "التقرير الشهري", icon: BarChart3, testid: "nav-monthly" },
    { to: "/evaluations", label: "تقييمات طفلي", icon: ClipboardList, testid: "nav-evaluations" },
    { to: "/invoices", label: "الفواتير", icon: Receipt, testid: "nav-invoices" },
    { to: "/iep", label: "خطة الطفل", icon: Target, testid: "nav-iep" },
    { to: "/sessions", label: "المواعيد", icon: CalendarDays, testid: "nav-sessions" },
    { to: "/videos", label: "مكتبة الفيديوهات", icon: Video, testid: "nav-videos" },
    { to: "/playlists", label: "قوائم التشغيل", icon: Layers, testid: "nav-playlists" },
    { to: "/emergency", label: "تنبيهات الطوارئ", icon: Siren, testid: "nav-emergency" },
  ];
}

function SideNav({ role, onNav }) {
  const items = navFor(role);
  return (
    <nav className="flex flex-col gap-1 p-4">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/"}
            data-testid={it.testid}
            onClick={onNav}
            className={({ isActive }) =>
              `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-base font-semibold transition-all ${
                isActive
                  ? "bg-sky-600 text-white shadow-md shadow-sky-100"
                  : "text-slate-700 hover:bg-sky-50 hover:text-sky-700"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <div className="relative w-9 h-9 shrink-0 rounded-xl overflow-hidden ring-2 ring-white shadow-sm">
                  {NAV_ART[it.to] && (
                    <img src={NAV_ART[it.to]} alt="" className="w-full h-full object-cover" />
                  )}
                  <div className={`absolute inset-0 flex items-center justify-center ${isActive ? "bg-sky-900/50" : "bg-slate-900/35 group-hover:bg-sky-700/45"} transition-colors`}>
                    <Icon className="w-4 h-4 text-white" strokeWidth={2.5} />
                  </div>
                </div>
                <span>{it.label}</span>
              </>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

export default function Layout() {
  const { user, logout, elder, setElder } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login?logout=1");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-72 shrink-0 border-l border-slate-200 bg-white flex-col">
        <div className="p-5 border-b border-slate-200 flex items-center gap-3">
          <img src="/logo.png" alt="مركز هابي داي" className="w-11 h-11 rounded-2xl shadow-sm bg-white p-1" />
          <div className="flex-1 min-w-0">
            <div className="font-black text-slate-900 text-lg">هابي داي</div>
            <div className="text-xs text-slate-500">{ROLE_LABEL[user.role]}</div>
          </div>
          <EmergencyBell />
          <InboxBell />
          <button
            onClick={doLogout}
            className="w-9 h-9 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 flex items-center justify-center transition"
            title="تسجيل الخروج"
            data-testid="header-logout-btn"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <SideNav role={user.role} />
        </div>
        <div className="p-4 border-t border-slate-200 space-y-2">
          <div className="px-3 py-2 rounded-lg bg-slate-50 text-sm">
            <div className="font-bold text-slate-800 truncate">{user.name}</div>
            <div className="text-xs text-slate-500 truncate" dir="ltr">{user.email}</div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1 h-11 text-sm gap-2"
              onClick={() => setElder(!elder)}
              data-testid="toggle-elder-mode"
            >
              <Type className="w-4 h-4" />
              {elder ? "خط عادي" : "خط كبير"}
            </Button>
            <Button
              variant="outline"
              className="h-11 text-sm gap-2 text-rose-700 hover:bg-rose-50"
              onClick={doLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              خروج
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile sheet */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-slate-200 h-16 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="مركز هابي داي" className="w-9 h-9 rounded-xl bg-white p-0.5" />
          <div className="font-black text-slate-900">هابي داي</div>
        </div>
        <div className="flex items-center gap-2">
          <EmergencyBell />
          <InboxBell />
          <Button size="icon" variant="outline" onClick={() => setElder(!elder)} data-testid="toggle-elder-mode-mobile">
            <Type className="w-4 h-4" />
          </Button>
          <Button size="icon" variant="outline" onClick={doLogout} className="text-rose-700 hover:bg-rose-50" data-testid="mobile-header-logout" title="تسجيل الخروج">
            <LogOut className="w-4 h-4" />
          </Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button size="icon" variant="outline" data-testid="mobile-nav-trigger">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 p-0">
              <div className="p-5 border-b border-slate-200">
                <div className="font-black text-lg">{user.name}</div>
                <div className="text-xs text-slate-500" dir="ltr">{user.email}</div>
                <div className="mt-1 text-xs text-sky-700 font-bold">{ROLE_LABEL[user.role]}</div>
              </div>
              <SideNav role={user.role} onNav={() => setOpen(false)} />
              <div className="p-4 border-t">
                <Button variant="outline" className="w-full gap-2 text-rose-700" onClick={doLogout} data-testid="mobile-logout">
                  <LogOut className="w-4 h-4" /> تسجيل الخروج
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <main className="flex-1 lg:mt-0 mt-16 p-4 lg:p-8 max-w-full overflow-x-hidden">
        {user.role === "admin" && (
          <div className="mb-6 hidden lg:block">
            <SmartSearchBar />
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
